// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import LanguageSelect from 'components/atoms/LanguageSelect';
import { IConChevronLeft } from 'components/atoms/icons/IconChevronLeft';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { IconReload } from 'components/atoms/icons/IconReload';
import { Navbar } from 'components/organisms/Navbar';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectUser } from 'redux/slices/userSlice';
import { RoleType } from 'util/Enums';
import './PrimaryTemplate.scss';
import useNetworkConnection from 'util/hooks/useNetworkConnection';
import { selectIsExporting } from 'redux/slices/historiesCsvExportSlice';
import { LoadingSpiner } from 'components/atoms/LoadingSpiner';
import { mergeClasses } from 'util/commons';

export interface PrimaryTemplateProps {
  children?: React.ReactNode;
  download?: VoidFunction;
  prevFunction?: () => void;
  className?: string;
  hasLogo?: boolean;
  h1?: string | null;
  prev?: string;
  keepSearchParamsOnPrev?: boolean;
  withPageReload?: boolean;
  // show site name, language select and navbar on right side of header
  showActions?: boolean;
  customName?: string;
  customCenterElement?: React.ReactNode;
  customRightElement?: React.ReactNode;
  headerSticky?: boolean;
}

export function PrimaryTemplate({
  children,
  download,
  withPageReload = true,
  h1,
  prev,
  keepSearchParamsOnPrev = false,
  prevFunction,
  className,
  hasLogo,
  showActions = true,
  customName,
  customCenterElement,
  customRightElement,
  headerSticky = false,
}: PrimaryTemplateProps) {
  const user = useSelector(selectUser);
  const labelSliceToDisplay = (
    label: string,
    maxNumOfCharacterDisplay = 20
  ) => {
    if (!label) {
      return '';
    }
    return label.length > maxNumOfCharacterDisplay
      ? `${label.slice(0, maxNumOfCharacterDisplay)}...`
      : label;
  };
  const { t } = useTranslation();

  const isOnline = useNetworkConnection();
  const isExporting = useSelector(selectIsExporting);
  const history = useHistory();

  const getCustomName = () => {
    if (customName != null) {
      return customName;
    }

    return user.role === RoleType.AdminOperator ||
      user.role === RoleType.BrandOwner
      ? user.operatorName
      : user.role === RoleType.AdminSite || user.role === RoleType.UserNormal
      ? user.siteName
      : user.role === RoleType.AdminTenant
      ? user.tenantName || ''
      : '';
  };

  const renderDownloadButton = () => {
    if (isExporting) {
      return (
        <div className="download">
          <LoadingSpiner width={24} height={24} color="#B7B7B7" />
        </div>
      );
    }

    if (!download) {
      return null;
    }

    return (
      <div className="download" onClick={download}>
        <IconDownload />
      </div>
    );
  };

  return (
    <div>
      <header
        className={mergeClasses('nav', {
          sticky: headerSticky,
        })}
      >
        <div className="nav-group .nav-left">
          {showActions && <>{renderDownloadButton()}</>}

          {(prev || prevFunction) && (
            <div
              className="prev"
              onClick={() => {
                if (prev) {
                  history.push(
                    prev +
                      (keepSearchParamsOnPrev ? window.location.search : '')
                  );
                } else {
                  prevFunction?.();
                }
              }}
            >
              <IConChevronLeft height={12} />
              <span>{t('common.return')}</span>
            </div>
          )}
        </div>

        <div className="nav-group nav-center">
          <h1>{h1}</h1>
          {hasLogo && <img src="/assets/images/logos.svg" alt="logo" />}
          {withPageReload && isOnline && (
            <span className="reload">
              <IconReload />
            </span>
          )}
          {customCenterElement}
        </div>

        <div className="nav-group nav-right">
          {showActions && (
            <>
              <div className="site-name" title={getCustomName()}>
                {labelSliceToDisplay(getCustomName(), 10)}
              </div>

              <div className="language">
                <LanguageSelect
                  classNames={{
                    wrapper: 'language-select',
                    dropdown: 'language-dropdown',
                  }}
                />
              </div>

              {<Navbar />}
            </>
          )}
          {customRightElement}
        </div>
      </header>

      <div className={mergeClasses('contents', className)}>{children}</div>
    </div>
  );
}
